"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Section, Typography } from "@/components/ui";
import { env } from "@/lib/environment";

interface Statistic {
  value: string;
  label: string;
}

type BenefitItem = string | { label: string; description?: string };

interface ConnectedTeamsContent {
  title: string;
  description?: string;
  benefits?: BenefitItem[];
  statistics?: Statistic[];
  ctaText?: string;
  ctaLink?: string;
  image: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
}

interface ConnectedTeamsSectionProps {
  content: ConnectedTeamsContent;
}

// Hook to detect when element is in viewport
function useIntersectionObserver(
  elementRef: React.RefObject<HTMLDivElement | null>,
  options = { threshold: 0.3 }
) {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsIntersecting(true);
      }
    }, options);

    observer.observe(element);

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [elementRef, options]);

  return isIntersecting;
}

// Counter animation component
function AnimatedCounter({ 
  targetValue, 
  duration = 1200,
  startAnimation 
}: { 
  targetValue: string; 
  duration?: number;
  startAnimation: boolean;
}) {
  const [count, setCount] = useState(0);
  
  // Extract numeric value and suffix
  // Normalize by removing thousands separators (commas, spaces) and extract full numeric token
  const normalizedValue = (targetValue || '').replace(/[,\s]+/g, '');
  const numericMatch = normalizedValue.match(/[\d.]+/);
  const targetNumber = numericMatch ? Number(numericMatch[0]) : 0;
  
  // Extract suffix by removing all digits, dots, commas, and spaces
  const suffix = (targetValue || '').replace(/[\d.,\s]/g, '');

  useEffect(() => {
    if (!startAnimation) return;
    
    setCount(0);
    const startTime = performance.now();
    let animationFrameId: number;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Smoother easing function (easeOutCubic)
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      
      // Round to nearest integer for smooth counting
      const currentCount = Math.round(easeOutCubic * targetNumber);
      
      setCount(currentCount);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        // Ensure we end exactly at target
        setCount(targetNumber);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [targetNumber, duration, startAnimation]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
}

export default function ConnectedTeamsSection({ content }: ConnectedTeamsSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(sectionRef, { threshold: 0.3 });

  return (
    <Section variant="default" background="transparent" className="bg-[#FFF8F0]">
      <div className="mx-auto" ref={sectionRef}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 pr-0 md:pr-4">
            <Typography variant="h2" as="h2" className="text-text-primary">
              {content.title}
            </Typography>
            <Typography variant="body" as="p" className="text-text-secondary ">{content.description}</Typography>

            {/* Benefits with Checkmarks - optional */}
            {content.benefits && content.benefits.length > 0 && (
            <div className="space-y-4">
              {content.benefits.map((benefit, index) => {
                const isObject = typeof benefit === "object";
                const label = isObject ? benefit.label : "";
                const description = isObject ? benefit.description : undefined;
                const text = typeof benefit === "string" ? benefit : "";
                const strongMatch = text.match(/^Strong\s+/);
                return (
                <div key={index} className="flex items-start gap-3">
                  <div className="shrink-0 mt-1">
                    <Image
                      src={`${env.NEXT_PUBLIC_CDN_URL}/images/industries/corporate/tick.DgpDqOZ0stAU.svg`}
                      alt="check mark"
                      title="check mark"
                      width={20}
                      height={20}
                      loading="lazy"
                    />
                  </div>
                  <Typography variant="body" as="p" className="text-text-primary">
                    {isObject ? (
                      <>
                        <span className="font-semibold">{label}</span>
                        {description != null && description !== "" && (
                          <span className="font-normal text-text-secondary"> - {description}</span>
                        )}
                      </>
                    ) : strongMatch ? (
                      <>
                        <strong>{strongMatch[0].trim()}</strong> {text.slice(strongMatch[0].length)}
                      </>
                    ) : (
                      text
                    )}
                  </Typography>
                </div>
                );
              })}
            </div>
            )}

            {/* Optional CTA button at bottom */}
            {content.ctaText && content.ctaLink && (
              <Link
                href={content.ctaLink}
                className="text-primary underline hover:text-accent hover:underline font-medium inline-flex items-center gap-1"
              >
                {content.ctaText}
              </Link>
            )}

            {/* Statistics with Counter Animation - optional */}
            {content.statistics && content.statistics.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 text-center md:text-left gap-8">
              {content.statistics.map((stat, index) => (
                <div key={index}>
                  <Typography variant="h3" as="p" className="text-[#FF6B35] text-6xl font-bold mb-2">
                    <AnimatedCounter 
                      targetValue={stat.value} 
                      duration={1200}
                      startAnimation={isVisible}
                    />
                  </Typography>
                  <Typography variant="body" as="p" className="text-stone-500 text-base font-normal">
                    {stat.label}
                  </Typography>
                </div>
              ))}
            </div>
            )}
          </div>

          {/* Right Image */}
          <div className="relative">
            <Image
              src={content.image.src}
              alt={content.image.alt}
              title={content.image.alt}
              width={content.image.width}
              height={content.image.height}
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

